import { useState } from 'react'
import {
  IonImg,
  IonSearchbar,
  IonButton,
  IonIcon,
  IonRow,
  IonCol,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/react'
import { barcodeOutline } from 'ionicons/icons'
import { useAutoAnimate } from '@formkit/auto-animate/react'

import placeholder from '../../../../resources/no-image-product.png'
import { useToast } from '../../../../contexts/toastContext'
import { usePostProducts } from '../../../../hooks/shopwith/products/queries'
import { canScanSku, scanSku } from '../../../../hooks/shopwith/utils'
import { useInfiniteData } from '../../../../hooks/use-infinite-data'
import ProductItem from '../../../../components/shopwith/product-item'
import CloseButton from '../../../../components/ui/buttons/close'

type Props = {
  onSelect?: (selectedProductIds: Array<string>) => void
}
function EmailProductSelect({ onSelect }: Props) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedProductIds, setSelectedProductIds] = useState<Array<string>>(
    []
  )
  const [animateRef] = useAutoAnimate<HTMLUListElement>()
  const setToast = useToast()

  const {
    data: products,
    fetchNextPage,
    hasNextPage,
  } = usePostProducts(searchTerm)
  const [ionInfiniteScrollRef, loadMoreProducts] =
    useInfiniteData(fetchNextPage)
  const flatProducts = products?.pages.flatMap((x) => x.data) ?? []

  const selectedProducts = flatProducts.filter((product) =>
    selectedProductIds.includes(product.objectId)
  )

  function handleSelectProduct(_: string, id: string) {
    const isSelected = selectedProductIds.includes(id)
    let ids = []

    if (isSelected) ids = selectedProductIds.filter((prod) => prod !== id)
    else ids = [...selectedProductIds, id]
    setSelectedProductIds(ids)
    onSelect && onSelect(ids)
  }

  function handleRemoveProduct(id: string) {
    const ids = selectedProductIds.filter((prod) => prod !== id)

    setSelectedProductIds(ids)
    onSelect && onSelect(ids)
  }

  async function handleScan() {
    const scan = await scanSku()
    if (!scan) {
      setToast({
        message: 'Something went wrong while scanning.',
        color: 'danger',
      })
      return
    }
    setSearchTerm(scan)
  }

  return (
    <>
      <div className="space-y-2">
        <h4 className="font-semibold text-gray-300">Your Picked Products:</h4>
        <ul
          ref={animateRef}
          className="flex min-h-[100px] flex-wrap gap-2 rounded-lg border border-white p-4"
        >
          {selectedProducts.map((product) => (
            <li key={product.objectId} className="relative h-24 w-20">
              <IonImg
                src={product.featuredImage ?? placeholder}
                className="h-full w-full rounded-lg object-cover shadow-sm"
              />
              <CloseButton
                inner
                onClick={() => handleRemoveProduct(product.objectId)}
              />
            </li>
          ))}
          {selectedProducts.length === 0 ? (
            <p className="w-full text-center text-slate-500">
              No products selected yet
            </p>
          ) : null}
        </ul>
      </div>
      {/* Product Search */}
      <div className="flex items-center gap-2">
        <IonSearchbar
          color="secondary"
          value={searchTerm}
          onIonChange={(e) => setSearchTerm(e.detail.value!)}
          placeholder="Search for products..."
          className="flex-1"
        />
        {canScanSku ? (
          <IonButton
            size="default"
            color="yellow"
            className="rounded-xl"
            onClick={handleScan}
          >
            <IonIcon icon={barcodeOutline} />
          </IonButton>
        ) : null}
      </div>
      {/* Product Grid */}
      <IonRow>
        {flatProducts?.map((product) => (
          <IonCol
            key={product.objectId}
            className="flex flex-col"
            sizeXs="6"
            sizeSm="4"
            sizeMd="3"
          >
            <ProductItem
              product={product}
              shouldHideDetail
              onProductSelect={handleSelectProduct}
              isSelected={selectedProducts.some(
                (p) => p.objectId === product.objectId
              )}
            />
          </IonCol>
        ))}
      </IonRow>
      <IonInfiniteScroll
        ref={ionInfiniteScrollRef}
        onIonInfinite={loadMoreProducts}
        threshold="100px"
        disabled={!hasNextPage}
      >
        <IonInfiniteScrollContent
          loadingSpinner="bubbles"
          loadingText="Loading more cool stuff..."
        />
      </IonInfiniteScroll>
    </>
  )
}

export { EmailProductSelect }
